// src/pages/Admin/Views/SellerManagement/SellerInfoSection.jsx
import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext'; // Import the useAuth hook
import { Button } from '../../../components/ui/button'; // Import shadcn/ui Button
import { Badge } from '../../../components/ui/badge';   // Import shadcn/ui Badge
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'; // Import shadcn/ui Card
import { useToast } from '../../../hooks/use-toast';
import { usePermissions } from '../../../hooks/usePermissions';
import { Copy } from 'lucide-react';
//const COIN_EDIT_ALLOWED_GROUPS = ['super-admin', 'finance-team'];

// Updated InfoRow to be more compact
const InfoRow = ({ label, value, children }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-200">
        <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{label}:</span>
            <span className="ml-2">{value}</span>
        </div>
        <div className="text-sm">
            {children}
        </div>
    </div>
);

const SellerInfoSection = ({ sellerData, onEditCoins, onViewContentIssues,sellerProfileId, onRefreshData }) => {
    const { user, token } = useAuth();
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // 'suspend', 'unsuspend', 'delete'
    const [confirmStep, setConfirmStep] = useState(0); // 1 = first warning, 2 = final confirmation

    //const canImpersonate = user?.groups?.includes('super-admin');
    const { toast } = useToast();
    const { hasPermission } = usePermissions();

    if (!sellerData) return null;

    //const canEditCoins = user?.groups?.some(group => COIN_EDIT_ALLOWED_GROUPS.includes(group));
    const commsHealth = true //sellerData.is_active && !sellerData.is_suspended;
    const isSuspended = sellerData.is_suspended;
    const isDeleted = sellerData.is_deleted;

    const handleCopyToClipboard = async (value, label) => {
        if (!value) return;

        try {
            if (!navigator?.clipboard?.writeText) {
                throw new Error('Clipboard API not available');
            }

            await navigator.clipboard.writeText(value);
            toast({
                title: 'Copied',
                description: `${label} copied to clipboard.`,
            });
        } catch (error) {
            console.error(`Failed to copy ${label}:`, error);
            toast({
                variant: 'destructive',
                title: 'Copy failed',
                description: `Could not copy ${label.toLowerCase()}.`,
            });
        }
    };

    const handleLoginAsSeller = async () => {
        setIsLoggingIn(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/manage-user/users/${sellerData.user_id}/impersonate-login-link`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to generate login link.');
            }

            const { login_url } = await response.json();
            window.open(`${process.env.REACT_APP_FE_URL}/${login_url}`, '_blank', 'noopener,noreferrer');
            toast({
              variant: 'success',
              title: 'Login Link Generated'
            }) 
            //.success("Login link generated successfully!");
        } catch (error) {
            console.error("Failed to log in as seller:", error);
            toast({
              variant: 'destructive',
              title: 'Login Link Failed to Generate'
            }) 
        } finally {
            setIsLoggingIn(false);
        }
    };

    const executeAccountAction = async (actionEndpoint, successMessage) => {
        setIsProcessing(true);
        const method = actionEndpoint === 'delete' ? 'DELETE' : 'POST';
        const url = actionEndpoint === 'delete' ? '' : `/${actionEndpoint}`;
        
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/manage-user/users/${sellerData.user_id}${url}`, {
                method: method,
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!response.ok) throw new Error('Action failed.');
            toast({ variant: 'success', title: successMessage });
            if (onRefreshData) onRefreshData(); // Re-fetch the user details
        } catch (error) {
            toast({ variant: 'destructive', title: `Failed to ${actionEndpoint} user.` });
        } finally {
            setIsProcessing(false);
            setPendingAction(null);
            setConfirmStep(0);
        }
    };

    const initiateAction = (action) => {
        setPendingAction(action);
        setConfirmStep(1);
    };

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CardTitle>Seller Info</CardTitle>
                                {isSuspended && <Badge variant="secondary" className='bg-yellow-600 text-white'>Suspended</Badge>}
                                {sellerData.is_deleted && <Badge variant="secondary" className='bg-red-600 text-white'>Deleted</Badge>}
                                {sellerData.signup_channel && <Badge variant="secondary" className='bg-brand-warm-gray text-white'>{sellerData.signup_channel} Signup</Badge>}
                            </div>
                            
                            <div className="flex gap-2">
                                {/* Log in button stays safe at the top */}
                                {hasPermission("cx:seller-account-login") && !sellerData.is_deleted && (
                                    <Button variant="secondary" size="sm" onClick={handleLoginAsSeller} disabled={isProcessing}>
                                        Log in as Seller
                                    </Button>
                                )}
                            </div>
                        </div>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                    <InfoRow label="User Id" value={sellerData.user_id || 'N/A'} />
                    <InfoRow label="Seller Profile Id" value={sellerProfileId || 'N/A'} />
                    {sellerData.prospect_id && <InfoRow label="Prospect ID" value={
                        <span className='inline-flex items-center gap-1 italic'>{sellerData.prospect_id} (Prospect Source: {sellerData.prospect_source})</span>
                        }>                        
                    </InfoRow>}
                    <InfoRow label="Name" value={sellerData.name || 'N/A'} />
                    <InfoRow label="Company" value={sellerData.company_name || 'N/A'} />
                    <InfoRow label="Signup Date" value={sellerData.seller_signup_date_ist || 'N/A'} />
                    <InfoRow
                        label="Email"
                        value={
                            <span className="inline-flex items-center gap-1">
                                <span>{sellerData.email || 'N/A'}</span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 p-0"
                                    onClick={() => handleCopyToClipboard(sellerData.email, 'Email')}
                                    disabled={!sellerData.email}
                                    aria-label="Copy seller email"
                                >
                                    <Copy className="h-3.5 w-3.5" />
                                </Button>
                            </span>
                        }
                    >
                        <Badge variant={sellerData.email_verified ? "default" : "secondary"}>
                            {sellerData.email_verified ? "Verified" : "Not Verified"}
                        </Badge>
                    </InfoRow>
                    <InfoRow
                        label="Phone"
                        value={
                            <span className="inline-flex items-center gap-1">
                                <span>{sellerData.phone || 'N/A'}</span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 p-0"
                                    onClick={() => handleCopyToClipboard(sellerData.phone, 'Phone number')}
                                    disabled={!sellerData.phone}
                                    aria-label="Copy seller phone number"
                                >
                                    <Copy className="h-3.5 w-3.5" />
                                </Button>
                            </span>
                        }
                    >
                        <Badge variant={sellerData.phone_number_verified ? "default" : "secondary"}>
                            {sellerData.phone_number_verified ? "Verified" : "Not Verified"}
                        </Badge>
                    </InfoRow>
                    <InfoRow label="Comms Health" value="">
                        <Badge variant={commsHealth ? "default" : "destructive"}>
                            {commsHealth ? 'Healthy' : 'Issues Detected'}
                        </Badge>
                    </InfoRow>
                    <InfoRow label="Location" value={sellerData.location_str || 'N/A'} />
                    <InfoRow label="Categories" value={sellerData.categories?.join('; ') || 'None'} />
                    <InfoRow label="Lead Purchases" value={sellerData.lead_purchases_count} />
                    <InfoRow label="Matches (7d/14d/90d)" value={`${sellerData.lead_matches_7d}/${sellerData.lead_matches_14d}/${sellerData.lead_matches_90d}`} />
                    <InfoRow label="Coins Balance" value={sellerData.coin_balance}>
                        {hasPermission("cx:adjust-coins") && (
                            <Button onClick={onEditCoins} variant="link" className="h-auto p-0">Edit</Button>
                        )}
                    </InfoRow>
                    <InfoRow label="Free Responses" value={sellerData.free_response_balance} />
                    <InfoRow label="Profile Score" className={sellerData.public_profile_completeness_score<25 ? "text-red-600" : "text-foreground"} value={`${sellerData.public_profile_completeness_score}%`} />
                    <InfoRow label="Public Profile" value="">
                        <Button asChild variant="link" className="h-auto p-0">
                            <a href={`${process.env.REACT_APP_FE_URL}/professional/${sellerData.public_profile_slug}`} target="_blank" rel="noopener noreferrer">View Profile</a>
                        </Button>
                    </InfoRow>
                    <InfoRow label="Content Issues" value={sellerData.public_profile_content_issues?.length || 0}>
                        <Button onClick={onViewContentIssues} variant="link" className="h-auto p-0">View</Button>
                    </InfoRow>
                    {/* UPDATED DANGER ZONE */}
                    {hasPermission("users:manage") && (
                        <div className="mt-8 pt-4 border-t border-red-200">
                            <h4 className="text-xs font-bold text-red-600 uppercase tracking-wider mb-3">Admin Actions</h4>
                            <div className="flex gap-3">
                                {isDeleted ? (
                                    // If deleted, ONLY show the restore option
                                    <Button variant="outline" size="sm" onClick={() => initiateAction('restore')} disabled={isProcessing} className="text-green-600 border-green-200 hover:bg-green-50">
                                        Restore User Account
                                    </Button>
                                ) : (
                                    // If active, show suspend and delete options
                                    <>
                                        {isSuspended ? (
                                            <Button variant="outline" size="sm" onClick={() => initiateAction('unsuspend')} disabled={isProcessing}>
                                                Unsuspend User
                                            </Button>
                                        ) : (
                                            <Button variant="outline" size="sm" onClick={() => initiateAction('suspend')} disabled={isProcessing} className="text-orange-600 border-orange-200 hover:bg-orange-50">
                                                Suspend User
                                            </Button>
                                        )}
                                        <Button variant="destructive" size="sm" onClick={() => initiateAction('delete')} disabled={isProcessing}>
                                            Delete User
                                        </Button>
                                    </>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                {isDeleted 
                                    ? "This user is currently in a 30-day pending deletion state. Restoring them grants full access."
                                    : "Suspending a user restricts their access. Deleting a user starts a 30-day permanent deletion timer."
                                }
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* DOUBLE CONFIRMATION MODAL */}
            {pendingAction && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-background p-6 rounded-lg shadow-lg max-w-md w-full border border-border">
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            {confirmStep === 1 ? 'Action Required' : 'Final Confirmation'}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            {confirmStep === 1 
                                ? `Are you sure you want to ${pendingAction} this user?` 
                                : `WARNING: Please confirm one more time. You are about to ${pendingAction} this user.`}
                        </p>
                        <div className="flex justify-end gap-3">
                            <Button variant="ghost" onClick={() => { setPendingAction(null); setConfirmStep(0); }}>
                                Cancel
                            </Button>
                            {confirmStep === 1 ? (
                                <Button 
                                    variant={pendingAction === 'delete' ? 'destructive' : 'default'} 
                                    onClick={() => setConfirmStep(2)}
                                >
                                    Yes, I'm sure
                                </Button>
                            ) : (
                                <Button 
                                    variant={pendingAction === 'delete' ? 'destructive' : 'default'} 
                                    onClick={() => executeAccountAction(pendingAction, `User ${pendingAction}ed successfully`)}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? 'Processing...' : `Absolutely ${pendingAction}`}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SellerInfoSection;
